import { useCallback, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import * as XLSX from 'xlsx';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  LinearProgress,
  Alert,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import { BuyAndBuildListDocument, useAddBuyAndBuildTargetMutation } from '../../../gql/generated/graphql';
import { MUI_RATING_SCALE } from '../BuyBuildPage';

interface ImportFromExcelDialogProps {
  open: boolean;
  onClose: () => void;
  companyId: string;
}

interface ExcelRow {
  [key: string]: string | number;
}

interface ParsedData {
  headers: string[];
  rows: ExcelRow[];
}

interface ColumnMapping {
  url: string;
  name: string;
  quality: string;
  tags: string;
}

interface ImportResult {
  row: number;
  success: boolean;
  error?: string;
  data: ExcelRow;
}

const EMPTY_MAPPING: ColumnMapping = {
  url: '',
  name: '',
  quality: '',
  tags: '',
};

export const ImportFromExcelDialog = ({ open, onClose, companyId }: ImportFromExcelDialogProps) => {
  const [parsedData, setParsedData] = useState<ParsedData | null>(null);
  const [columnMapping, setColumnMapping] = useState<ColumnMapping>(EMPTY_MAPPING);
  const [importing, setImporting] = useState(false);
  const [importResults, setImportResults] = useState<ImportResult[]>([]);
  const [currentStep, setCurrentStep] = useState<'upload' | 'mapping' | 'preview' | 'importing' | 'results'>('upload');

  const [addBuyAndBuildTarget] = useAddBuyAndBuildTargetMutation();

  // Auto-detect column mapping based on common column names
  const autoDetectColumns = useCallback((headers: string[]): ColumnMapping => {
    const lowerHeaders = headers.map(h => h.toLowerCase().trim());

    const findColumn = (patterns: string[]) => {
      for (const pattern of patterns) {
        const index = lowerHeaders.findIndex(h => h.includes(pattern));
        if (index !== -1) return headers[index];
      }
      return '';
    };

    return {
      url: findColumn(['url', 'domain', 'website', 'link', 'web']),
      name: findColumn(['name', 'company', 'title', 'firm']),
      quality: findColumn(['quality', 'score', 'rating', 'relevance', 'weight']),
      tags: findColumn(['tag', 'tags', 'category', 'categories', 'label']),
    };
  }, []);

  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      const file = acceptedFiles[0];
      if (!file) return;

      const reader = new FileReader();
      reader.onload = e => {
        try {
          const data = e.target?.result;
          const workbook = XLSX.read(data, { type: 'binary' });
          const sheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[sheetName];
          const jsonData = XLSX.utils.sheet_to_json<ExcelRow>(worksheet, { header: 1 });

          if (jsonData.length < 2) {
            alert('Excel file must contain at least a header row and one data row');
            return;
          }

          const headers = jsonData[0] as unknown as string[];
          const rows = jsonData.slice(1).filter(row => {
            // Filter out empty rows
            return Object.values(row).some(val => val !== null && val !== undefined && val !== '');
          }) as ExcelRow[];

          // Convert array rows to objects with headers as keys
          const rowObjects = rows.map(row => {
            const obj: ExcelRow = {};
            headers.forEach((header, index) => {
              obj[header] = (row as any)[index];
            });
            return obj;
          });

          setParsedData({ headers, rows: rowObjects });
          const detectedMapping = autoDetectColumns(headers);
          setColumnMapping(detectedMapping);
          setCurrentStep('mapping');
        } catch (error) {
          console.error('Error parsing Excel file:', error);
          alert('Failed to parse Excel file. Please ensure it is a valid Excel file.');
        }
      };
      reader.readAsBinaryString(file);
    },
    [autoDetectColumns]
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
      'application/vnd.ms-excel': ['.xls'],
    },
    multiple: false,
  });

  const handleMappingChange = (field: keyof ColumnMapping, value: string) => {
    setColumnMapping(prev => ({ ...prev, [field]: value }));
  };

  const handlePreview = () => {
    if (!columnMapping.url) {
      alert('URL column mapping is required');
      return;
    }
    setCurrentStep('preview');
  };

  const handleImport = async () => {
    if (!parsedData || !columnMapping.url) return;

    setCurrentStep('importing');
    setImporting(true);
    setImportResults([]);

    const results: ImportResult[] = [];

    for (let i = 0; i < parsedData.rows.length; i++) {
      const row = parsedData.rows[i];
      const url = row[columnMapping.url]?.toString() || '';

      if (!url) {
        results.push({
          row: i + 1,
          success: false,
          error: 'Missing URL',
          data: row,
        });
        continue;
      }

      try {
        // Extract quality/rating if provided
        let quality = 1.0; // Default to max quality
        if (columnMapping.quality && row[columnMapping.quality]) {
          const qualityValue = parseFloat(row[columnMapping.quality].toString());
          if (!isNaN(qualityValue)) {
            // Normalize to 0-1 range (assuming input is 0-5 or 0-10 or percentage)
            if (qualityValue <= 1) {
              quality = qualityValue;
            } else if (qualityValue <= MUI_RATING_SCALE) {
              quality = qualityValue / MUI_RATING_SCALE;
            } else if (qualityValue <= 10) {
              quality = qualityValue / 10;
            } else if (qualityValue <= 100) {
              quality = qualityValue / 100;
            }
          }
        }

        // Extract tags if provided
        const tags = columnMapping.tags && row[columnMapping.tags] ? row[columnMapping.tags].toString() : '';

        const formattedUrl = url.startsWith('http') ? url : `https://${url}`;

        await addBuyAndBuildTarget({
          variables: {
            softwareCompanyId: parseInt(companyId),
            targetCompanyUrl: formattedUrl,
            quality,
            setManualScore: true,
            sourcescrubTags: tags || undefined,
          },
          refetchQueries: [
            {
              query: BuyAndBuildListDocument,
              variables: {
                companyId: parseInt(companyId),
                first: 10,
              },
            },
          ],
        });

        results.push({
          row: i + 1,
          success: true,
          data: row,
        });
      } catch (error: any) {
        results.push({
          row: i + 1,
          success: false,
          error: error.message || 'Unknown error',
          data: row,
        });
      }

      setImportResults([...results]);
    }

    setImporting(false);
    setCurrentStep('results');
  };

  const handleClose = () => {
    setParsedData(null);
    setColumnMapping(EMPTY_MAPPING);
    setImportResults([]);
    setCurrentStep('upload');
    onClose();
  };

  const successCount = importResults.filter(r => r.success).length;
  const errorCount = importResults.filter(r => !r.success).length;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth>
      <DialogTitle>Import Buy & Build Targets from Excel</DialogTitle>
      <DialogContent>
        {/* Step 1: Upload */}
        {currentStep === 'upload' && (
          <Box
            {...getRootProps()}
            sx={{
              border: '2px dashed',
              borderColor: isDragActive ? 'primary.main' : 'grey.300',
              borderRadius: 2,
              p: 4,
              textAlign: 'center',
              cursor: 'pointer',
              bgcolor: isDragActive ? 'action.hover' : 'background.paper',
              '&:hover': {
                bgcolor: 'action.hover',
                borderColor: 'primary.main',
              },
            }}
          >
            <input {...getInputProps()} />
            <CloudUploadIcon sx={{ fontSize: 64, color: 'primary.main', mb: 2 }} />
            <Typography variant="h6" gutterBottom>
              {isDragActive ? 'Drop the Excel file here' : 'Drag & drop an Excel file here'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              or click to select a file
            </Typography>
            <Typography variant="caption" display="block" sx={{ mt: 2 }} color="text.secondary">
              Supported formats: .xlsx, .xls
            </Typography>
          </Box>
        )}

        {/* Step 2: Column Mapping */}
        {currentStep === 'mapping' && parsedData && (
          <Box>
            <Alert severity="info" sx={{ mb: 3 }}>
              Map your Excel columns to the required fields. URL is required, others are optional.
            </Alert>
            <Box display="flex" flexDirection="column" gap={2}>
              <FormControl fullWidth required>
                <InputLabel>URL / Domain Column</InputLabel>
                <Select
                  value={columnMapping.url}
                  onChange={e => handleMappingChange('url', e.target.value)}
                  label="URL / Domain Column"
                >
                  <MenuItem value="">
                    <em>Select a column</em>
                  </MenuItem>
                  {parsedData.headers.map(header => (
                    <MenuItem key={header} value={header}>
                      {header}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl fullWidth>
                <InputLabel>Company Name Column (optional)</InputLabel>
                <Select
                  value={columnMapping.name}
                  onChange={e => handleMappingChange('name', e.target.value)}
                  label="Company Name Column (optional)"
                >
                  <MenuItem value="">
                    <em>None</em>
                  </MenuItem>
                  {parsedData.headers.map(header => (
                    <MenuItem key={header} value={header}>
                      {header}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl fullWidth>
                <InputLabel>Quality/Rating Column (optional)</InputLabel>
                <Select
                  value={columnMapping.quality}
                  onChange={e => handleMappingChange('quality', e.target.value)}
                  label="Quality/Rating Column (optional)"
                >
                  <MenuItem value="">
                    <em>None (default: max quality)</em>
                  </MenuItem>
                  {parsedData.headers.map(header => (
                    <MenuItem key={header} value={header}>
                      {header}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl fullWidth>
                <InputLabel>Tags/Categories Column (optional)</InputLabel>
                <Select
                  value={columnMapping.tags}
                  onChange={e => handleMappingChange('tags', e.target.value)}
                  label="Tags/Categories Column (optional)"
                >
                  <MenuItem value="">
                    <em>None</em>
                  </MenuItem>
                  {parsedData.headers.map(header => (
                    <MenuItem key={header} value={header}>
                      {header}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          </Box>
        )}

        {/* Step 3: Preview */}
        {currentStep === 'preview' && parsedData && (
          <Box>
            <Alert severity="success" sx={{ mb: 2 }}>
              Ready to import {parsedData.rows.length} companies
            </Alert>
            <TableContainer component={Paper} sx={{ maxHeight: 400 }}>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>#</TableCell>
                    <TableCell>URL</TableCell>
                    {columnMapping.name && <TableCell>Name</TableCell>}
                    {columnMapping.quality && <TableCell>Quality</TableCell>}
                    {columnMapping.tags && <TableCell>Tags</TableCell>}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {parsedData.rows.slice(0, 10).map((row, index) => (
                    <TableRow key={index}>
                      <TableCell>{index + 1}</TableCell>
                      <TableCell>{row[columnMapping.url]}</TableCell>
                      {columnMapping.name && <TableCell>{row[columnMapping.name]}</TableCell>}
                      {columnMapping.quality && <TableCell>{row[columnMapping.quality]}</TableCell>}
                      {columnMapping.tags && <TableCell>{row[columnMapping.tags]}</TableCell>}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            {parsedData.rows.length > 10 && (
              <Typography variant="caption" sx={{ mt: 1, display: 'block' }} color="text.secondary">
                Showing first 10 of {parsedData.rows.length} rows
              </Typography>
            )}
          </Box>
        )}

        {/* Step 4: Importing */}
        {currentStep === 'importing' && (
          <Box>
            <Typography variant="body1" gutterBottom>
              Importing companies... {importResults.length} of {parsedData?.rows.length || 0}
            </Typography>
            <LinearProgress
              variant="determinate"
              value={(importResults.length / (parsedData?.rows.length || 1)) * 100}
              sx={{ mb: 2 }}
            />
            <Box sx={{ maxHeight: 300, overflow: 'auto' }}>
              {importResults.map((result, index) => (
                <Box key={index} display="flex" alignItems="center" gap={1} mb={1}>
                  {result.success ? (
                    <CheckCircleIcon color="success" fontSize="small" />
                  ) : (
                    <ErrorIcon color="error" fontSize="small" />
                  )}
                  <Typography variant="body2">
                    Row {result.row}: {result.success ? 'Success' : `Failed - ${result.error}`}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>
        )}

        {/* Step 5: Results */}
        {currentStep === 'results' && (
          <Box>
            <Box display="flex" gap={2} mb={3}>
              <Chip
                icon={<CheckCircleIcon />}
                label={`${successCount} Successful`}
                color="success"
                variant="outlined"
              />
              <Chip icon={<ErrorIcon />} label={`${errorCount} Failed`} color="error" variant="outlined" />
            </Box>
            {errorCount > 0 && (
              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  Failed Imports:
                </Typography>
                <Box sx={{ maxHeight: 300, overflow: 'auto' }}>
                  {importResults
                    .filter(r => !r.success)
                    .map((result, index) => (
                      <Alert key={index} severity="error" sx={{ mb: 1 }}>
                        Row {result.row}: {result.error}
                      </Alert>
                    ))}
                </Box>
              </Box>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        {currentStep === 'upload' && <Button onClick={handleClose}>Cancel</Button>}
        {currentStep === 'mapping' && (
          <>
            <Button onClick={() => setCurrentStep('upload')}>Back</Button>
            <Button onClick={handlePreview} variant="contained" disabled={!columnMapping.url}>
              Preview
            </Button>
          </>
        )}
        {currentStep === 'preview' && (
          <>
            <Button onClick={() => setCurrentStep('mapping')}>Back</Button>
            <Button onClick={handleImport} variant="contained" disabled={importing}>
              Import {parsedData?.rows.length} Companies
            </Button>
          </>
        )}
        {currentStep === 'results' && (
          <Button onClick={handleClose} variant="contained">
            Close
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};
